import { useQuery, useQueryClient } from "@tanstack/react-query"
import { createContext, useCallback, useContext, useEffect, type ReactNode } from "react"
import { t } from "./i18n"
import { sdk } from "./medusa"
import { forgetQueries } from "./query-persist"

export type HqUser = { id: string; email: string; first_name?: string | null; last_name?: string | null; metadata?: Record<string, unknown> | null }

type AuthState = {
  user: HqUser | null
  loading: boolean
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

/** Who is signed in, read from Medusa (GET /admin/users/me). A 401 simply means "nobody". */
export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const me = useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      try {
        const { user } = await sdk.admin.user.me()
        return user as HqUser
      } catch (error) {
        if ((error as { status?: number }).status === 401) {
          return null
        }
        throw error
      }
    },
    retry: false,
    staleTime: 5 * 60 * 1000,
  })

  // Nobody signed in (an expired session): what the phone kept from the last session goes too.
  useEffect(() => {
    if (me.data === null) forgetQueries()
  }, [me.data])

  const signIn = useCallback(
    async (email: string, password: string) => {
      const result = await sdk.auth.login("user", "emailpass", { email, password })
      // A plain token means signed in. Anything else is a redirect, a second factor or an email
      // verification step — none of which is set up for High Club admins yet.
      if (typeof result !== "string") {
        await sdk.auth.logout().catch(() => undefined)
        throw new Error(t("This account needs an extra sign-in step HQ does not support yet. Use Medusa admin."))
      }
      await queryClient.invalidateQueries({ queryKey: ["me"] })
    },
    [queryClient]
  )

  const signOut = useCallback(async () => {
    await sdk.auth.logout()
    forgetQueries()
    queryClient.clear()
    await queryClient.invalidateQueries({ queryKey: ["me"] })
  }, [queryClient])

  return (
    <AuthContext.Provider value={{ user: me.data ?? null, loading: me.isLoading, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const value = useContext(AuthContext)
  if (!value) {
    throw new Error("useAuth must be used inside <AuthProvider>")
  }
  return value
}
